import React, { useMemo, useState } from "react";
import { useCart } from "@/context/CartContext";
import { useNavigate } from "react-router-dom";

const ROASTS = [
  { key: "light", label: "Light", note: "Floral · Bright · Tea-like" },
  { key: "medium", label: "Medium", note: "Balanced · Caramel · Rounded" },
  { key: "dark", label: "Dark", note: "Chocolate · Smoky · Full-bodied" },
];
const GRINDS = [
  { key: "whole-bean", label: "Whole Bean" },
  { key: "coarse", label: "Coarse", sub: "French Press" },
  { key: "medium", label: "Medium", sub: "Drip / Pour Over" },
  { key: "fine", label: "Fine", sub: "Espresso · Filter" },
];

// Pricing: Arabica ₹1200/kg (₹1.20/g), Robusta ₹800/kg (₹0.80/g), Chicory ₹200/kg (₹0.20/g)
function computePrice(arabica, robusta, chicory, weight, roastKey) {
  const perG = (arabica / 100) * 1.20 + (robusta / 100) * 0.80 + (chicory / 100) * 0.20;
  const roastAdd = roastKey === "dark" ? 0.08 : roastKey === "light" ? 0.05 : 0;
  return Math.round((perG + roastAdd) * weight);
}

const PRESETS = [
  { key: "signature", label: "Signature", a: 70, r: 30, c: 0 },
  { key: "espresso", label: "Espresso Base", a: 60, r: 40, c: 0 },
  { key: "filter", label: "South Indian Filter", a: 53, r: 27, c: 20 },
  { key: "kaapi", label: "Traditional Kaapi", a: 40, r: 30, c: 30 },
  { key: "pure", label: "Pure Arabica", a: 100, r: 0, c: 0 },
];

export default function BlendBuilder() {
  const [arabica, setArabica] = useState(70);
  const [chicory, setChicory] = useState(0);
  const [roast, setRoast] = useState("medium");
  const [grind, setGrind] = useState("whole-bean");
  const [weight, setWeight] = useState(500);
  const [name, setName] = useState("");
  const { addItem } = useCart();
  const nav = useNavigate();

  // Clamp so total <= 100. Robusta fills the rest.
  const robusta = Math.max(0, 100 - arabica - chicory);
  const price = useMemo(
    () => computePrice(arabica, robusta, chicory, weight, roast),
    [arabica, robusta, chicory, weight, roast]
  );

  const applyPreset = (p) => {
    setArabica(p.a);
    setChicory(p.c);
  };

  const onArabica = (val) => {
    // Ensure arabica + chicory <= 100
    if (val + chicory > 100) setChicory(100 - val);
    setArabica(val);
  };
  const onChicory = (val) => {
    if (val + arabica > 100) setArabica(100 - val);
    setChicory(val);
  };

  const handleAdd = () => {
    const label = name.trim() || `Signature ${arabica}/${robusta}${chicory ? `/${chicory}c` : ""}`;
    addItem({
      name: label,
      price_inr: price,
      quantity: 1,
      weight_g: weight,
      grind,
      image_url: "https://images.pexels.com/photos/29795384/pexels-photo-29795384.jpeg",
      is_custom: true,
      custom_spec: { arabica, robusta, chicory, roast, grind, weight_g: weight, name: label },
    });
  };

  return (
    <div className="max-w-[1440px] mx-auto px-6 md:px-12 pt-32 pb-24">
      <div className="mb-14">
        <div className="label-mono mb-4 text-[var(--primary)]">॥ Bespoke · The Blend Builder ॥</div>
        <h1 className="slogan-hd text-[38px] md:text-[68px] leading-[0.95] tracking-tight">
          CUSTOMISED BLEND COFFEE
        </h1>
        <p className="slogan-hd-sub text-xl md:text-2xl mt-3">( blend coffee of your own )</p>
        <p className="mt-6 text-[var(--text-secondary)] max-w-2xl leading-relaxed">
          Three ingredients. One signature. Balance Arabica, Robusta and Chicory — the traditional
          South Indian filter-coffee trinity — then pick your roast, grind and weight.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
        {/* Left: controls */}
        <div className="md:col-span-8 border border-[var(--border)] bg-[var(--surface)] p-8 md:p-10 space-y-12">
          {/* Presets */}
          <div>
            <div className="label-mono mb-3">Start From</div>
            <div className="flex gap-2 flex-wrap">
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  onClick={() => applyPreset(p)}
                  className="px-4 py-2 border border-[var(--border)] hover:border-[var(--primary)] label-mono text-[var(--text-secondary)] hover:text-[var(--primary)] transition-colors"
                  data-testid={`preset-${p.key}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Composition */}
          <div>
            <div className="flex justify-between items-baseline mb-6">
              <div>
                <div className="label-mono mb-1">01 · Composition</div>
                <div className="font-serif text-2xl">The three-ingredient balance</div>
              </div>
              <div className="font-mono text-sm text-[var(--text-secondary)]">Total must be 100%</div>
            </div>

            {/* Stacked ratio bar */}
            <div className="mb-8">
              <div className="h-3 flex overflow-hidden border border-[var(--border-strong)]" data-testid="ratio-bar" style={{background: "var(--surface-warm)", boxShadow: "inset 0 1px 3px rgba(59,42,30,0.15)"}}>
                <div style={{ width: `${arabica}%`, background: "linear-gradient(180deg, #FF5C5F, #A3152A)" }} className="transition-[width] duration-300" />
                <div style={{ width: `${robusta}%`, background: "linear-gradient(180deg, #A9734A, #6B4423)" }} className="transition-[width] duration-300" />
                <div style={{ width: `${chicory}%`, background: "linear-gradient(180deg, #8BE3A9, #3FA866)" }} className="transition-[width] duration-300" />
              </div>
              <div className="grid grid-cols-3 mt-3 font-mono text-[10px] label-mono">
                <span><span className="inline-block w-2 h-2 mr-2 align-middle" style={{background:"#E5383F"}} />Arabica</span>
                <span className="text-center"><span className="inline-block w-2 h-2 mr-2 align-middle" style={{background:"#A9734A"}} />Robusta</span>
                <span className="text-right"><span className="inline-block w-2 h-2 mr-2 align-middle" style={{background:"#5CCB85"}} />Chicory</span>
              </div>
            </div>

            {/* Arabica slider */}
            <div className="mb-8">
              <div className="flex justify-between items-baseline mb-3">
                <div>
                  <div className="label-mono text-[var(--primary)]">Arabica</div>
                  <div className="text-xs text-[var(--text-muted)] mt-1">Smooth · aromatic · higher acidity</div>
                </div>
                <div className="font-mono text-3xl">{arabica}<span className="text-lg text-[var(--text-muted)]">%</span></div>
              </div>
              <input
                type="range"
                min="0" max="100" step="5"
                value={arabica}
                onChange={(e) => onArabica(+e.target.value)}
                className="slider-gold"
                data-testid="blend-slider-arabica"
              />
            </div>

            {/* Chicory slider */}
            <div className="mb-8">
              <div className="flex justify-between items-baseline mb-3">
                <div>
                  <div className="label-mono" style={{ color: "#5CCB85" }}>Chicory</div>
                  <div className="text-xs text-[var(--text-muted)] mt-1">Nutty · earthy · builds body & crema (caffeine-free)</div>
                </div>
                <div className="font-mono text-3xl">{chicory}<span className="text-lg text-[var(--text-muted)]">%</span></div>
              </div>
              <input
                type="range"
                min="0" max="50" step="5"
                value={chicory}
                onChange={(e) => onChicory(+e.target.value)}
                className="slider-gold"
                data-testid="blend-slider-chicory"
              />
              <div className="flex justify-between label-mono text-[10px] mt-2 text-[var(--text-muted)]">
                <span>0% · Pure coffee</span><span>50% · Heavy filter-style</span>
              </div>
            </div>

            {/* Robusta (computed) */}
            <div className="flex justify-between items-baseline pt-6 border-t border-[var(--border)]">
              <div>
                <div className="label-mono text-[var(--accent)]">Robusta <span className="text-[var(--text-muted)] text-[9px] normal-case tracking-normal">(auto-filled)</span></div>
                <div className="text-xs text-[var(--text-muted)] mt-1">Bold · earthy · rich crema · higher caffeine</div>
              </div>
              <div className="font-mono text-3xl">{robusta}<span className="text-lg text-[var(--text-muted)]">%</span></div>
            </div>
          </div>

          {/* Roast */}
          <div>
            <div className="label-mono mb-1">02 · Roast Depth</div>
            <div className="font-serif text-2xl mb-6">How dark should we take it?</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {ROASTS.map((r) => (
                <button
                  key={r.key}
                  onClick={() => setRoast(r.key)}
                  className={`p-5 text-left border transition-colors ${
                    roast === r.key ? "border-[var(--primary)] bg-[var(--surface-2)]" : "border-[var(--border)] hover:border-[var(--text-muted)]"
                  }`}
                  data-testid={`roast-${r.key}`}
                >
                  <div className="font-serif text-xl">{r.label}</div>
                  <div className="label-mono text-[10px] mt-2">{r.note}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Grind */}
          <div>
            <div className="label-mono mb-1">03 · Grind</div>
            <div className="font-serif text-2xl mb-6">Match your brewer.</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {GRINDS.map((g) => (
                <button
                  key={g.key}
                  onClick={() => setGrind(g.key)}
                  className={`p-4 text-left border transition-colors ${
                    grind === g.key ? "border-[var(--primary)] bg-[var(--surface-2)]" : "border-[var(--border)]"
                  }`}
                  data-testid={`grind-${g.key}`}
                >
                  <div className="font-serif text-base">{g.label}</div>
                  {g.sub && <div className="label-mono text-[9px] mt-1">{g.sub}</div>}
                </button>
              ))}
            </div>
          </div>

          {/* Weight */}
          <div>
            <div className="label-mono mb-1">04 · Weight</div>
            <div className="font-serif text-2xl mb-6">How much do you need?</div>
            <div className="grid grid-cols-3 gap-3">
              {[250, 500, 1000].map((w) => (
                <button
                  key={w}
                  onClick={() => setWeight(w)}
                  className={`p-5 border transition-colors ${
                    weight === w ? "border-[var(--primary)] bg-[var(--surface-2)]" : "border-[var(--border)]"
                  }`}
                  data-testid={`weight-${w}`}
                >
                  <div className="font-mono text-2xl">{w >= 1000 ? "1 kg" : `${w}g`}</div>
                  <div className="label-mono text-[9px] mt-1 text-[var(--text-muted)]">
                    ≈ {Math.round(w / 15)} cups
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Name */}
          <div>
            <div className="label-mono mb-3">05 · Name your blend (optional)</div>
            <input
              className="input-dark"
              placeholder="e.g., Sunday Morning"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={40}
              data-testid="blend-name-input"
            />
          </div>
        </div>

        {/* Right: summary */}
        <aside className="md:col-span-4 md:sticky md:top-24 h-fit border border-[var(--border)] bg-[var(--surface-2)] p-8">
          <div className="label-mono mb-6">Live Spec Sheet</div>

          <div className="space-y-4 font-mono text-sm">
            <Row k="ARABICA" v={`${arabica}%`} />
            <Row k="ROBUSTA" v={`${robusta}%`} />
            <Row k="CHICORY" v={`${chicory}%`} />
            <Row k="ROAST" v={roast.toUpperCase()} />
            <Row k="GRIND" v={grind.replace("-", " ").toUpperCase()} />
            <Row k="WEIGHT" v={weight >= 1000 ? "1 KG" : `${weight} G`} />
          </div>

          <div className="mt-8 pt-6 border-t border-[var(--border)]">
            <div className="label-mono mb-2">Estimated Price</div>
            <div className="font-mono text-5xl text-[var(--primary)]" data-testid="blend-price">₹{price}</div>
            <div className="label-mono text-[10px] mt-2 text-[var(--text-muted)]">Roast-to-order · Ships in 72h</div>
          </div>

          <button onClick={handleAdd} className="btn-highlight w-full mt-8" data-testid="blend-add-to-cart">
            Add to Cart
          </button>
          <button onClick={() => { handleAdd(); nav("/checkout"); }} className="btn-outline w-full mt-3" data-testid="blend-buy-now">
            Buy Now
          </button>
        </aside>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between border-b border-[var(--border)] pb-2">
      <span className="text-[var(--text-muted)] text-[11px] tracking-widest">{k}</span>
      <span className="text-[var(--text)]">{v}</span>
    </div>
  );
}
